import React, { useMemo } from "react";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { SurveyDefinition } from "./survey";
import { CollectionGroupDetailsDto } from "../../common/service/server/dto/CollectionGroupDto";
import { SurveyAnswerFn, SurveyAnswers, SurveyResponses } from "../../common/hooks/api/useSurvey";
import { consumerCollectionSurveyName } from "./consumerCollectionCategoriesSurvey";
import { Api } from "../../common/service/Api";
import { OrganizationPublicId } from "../../common/service/server/Types";
import { useDataRequests } from "../../common/hooks/api";
import { NetworkRequestState } from "../../common/models";
import { useQueryClient } from "@tanstack/react-query";

export const SENSITIVE_DATA_SURVEY_NAME = "sensitive-data-survey";
export const SENSITIVE_DATA_COLLECTION_SLUG = "sensitive-data-collection";

const PHYSICAL_MENTAL_HEALTH_QUESTION_SLUG = "physical-mental-health-condition";
const PHYSICAL_MENTAL_HEALTH_CG_QUESTION_SLUG =
  "physical-mental-health-condition-collection-groups";
export const physicalOrMentalHealthQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: PHYSICAL_MENTAL_HEALTH_QUESTION_SLUG,
    question:
      "Certain privacy laws recently updated their requirements concerning consumer health data, so we need to ask a new question related to these changes: Does your business use any consumer data it collects or stores to identify a consumer's physical or mental health condition or diagnosis?",
    helpText:
      "This includes any data related to exercise, fitness, nutrition, reproductive or sexual health, and gender-affirming health data, if your business uses such data to identify anything related to the consumer’s physical or mental health.",
  };
};

export const collectionGroupQuestion = (
  slug: string,
  groups: CollectionGroupDetailsDto[],
): SurveyQuestionProps => {
  const options = groups.map((cg) => ({
    key: cg.id,
    label: cg.name,
  }));

  return {
    type: "checkboxes",
    slug,
    question: "From which of the following groups do you collect this information?",
    emptyText: "There are no collection groups available.",
    options,
  };
};

export const EXPANDED_SENSITIVE_DATA_QUESTION_SLUG = "expanded-sensitive-data";
const EXPANDED_SENSITIVE_DATA_CG_QUESTION_SLUG = "expanded-sensitive-data-collection-groups";

export const expandedSensitiveDataQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: EXPANDED_SENSITIVE_DATA_QUESTION_SLUG,
    question: (
      <>
        <p>
          We previously asked if your business collects sensitive data, like ethnic origin or
          religious beliefs. New, expanded definitions cover data that reveals sensitive
          information. Does your business collect or access personal data that reveals any of the
          following?
        </p>
        <ul>
          <li>Racial or ethnic origin</li>
          <li>Religious beliefs</li>
          <li>Mental or physical health condition or diagnosis</li>
          <li>Sex life or sexual orientation</li>
          <li>Citizenship or immigration status</li>
        </ul>
      </>
    ),
  };
};

export const expandedSensitiveDataQuestionByGroup = (
  collectionGroup: CollectionGroupDetailsDto,
  initialPic?: Record<string, string[]>,
): SurveyQuestionProps => {
  // TODO: For whatever reason, not all of the PICs below have more stable, human-readable keys
  // so this will be remedied shortly with the POL-3259 changes.
  const options = [
    {
      key: "80a45c41-9d19-4d63-b5f4-d72f8aa436c0",
      label: "Race, color, or ethnic origin",
    },
    {
      key: "0fd256d6-d131-48a6-9e05-67788c8bd3ac",
      label: "Religion or philosophical beliefs",
    },
    {
      key: "d0fb8729-664c-4c85-af5b-92f7f81ffad8",
      label: "Physical or mental health conditions or diagnosis",
    },
    {
      key: "63443ea7-af53-40dd-a41c-06896c79799f",
      label: "Sexual orientation",
    },
    {
      key: "2d094766-11a2-4f2f-a0b1-905eac8b86d2",
      label: "Citizenship or immigration status",
    },
  ].map((opt) => {
    let title = "";
    if (
      initialPic &&
      initialPic[collectionGroup.id] &&
      initialPic[collectionGroup.id].includes(opt.key)
    ) {
      title =
        "You previously added this to your data map. If you no longer collect this data, unselect this category.";
    }
    return {
      ...opt,
      title: (checked) => (checked ? title : ""),
    };
  });
  return {
    type: "checkboxes",
    slug: SENSITIVE_DATA_COLLECTION_SLUG,
    surveyOverride: `consumer-data-collection-${collectionGroup.id}`,
    question: `Which of the following information categories do you collect for ${collectionGroup.name}?`,
    options,
  };
};

const VICTIM_OF_CRIME_QUESTION_SLUG = "victim-of-a-crime";
const VICTIM_OF_CRIME_CG_QUESTION_SLUG = "victim-of-a-crime-collection-groups";
export const crimeVictimQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: VICTIM_OF_CRIME_QUESTION_SLUG,
    question:
      "Does your business collect any data concerning an individual’s status as a victim of a crime?",
  };
};

const GEOFENCING_QUESTION_SLUG = "geofencing";
const GEOFENCING_CG_QUESTION_SLUG = "geofencing-collection-groups";
export const geofencingQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: GEOFENCING_QUESTION_SLUG,
    question:
      'There are new privacy restrictions related to "geofencing," which is the practice of electronically establishing a virtual boundary around a physical location to track the consumer’s geolocation within a specific proximity of that location. Does your business use geofencing to track a consumer’s location within one thousand seven hundred fifty (1,750) feet of any mental health facility or reproductive or sexual health facility?',
  };
};

export const isGroupSelected = (
  answers: SurveyResponses,
  groupId: string,
  slug: string,
): string => {
  try {
    const res = JSON.parse(answers[`${slug}-collection-groups`] ?? "[]");
    return Array.isArray(res) && res.includes(groupId) ? "true" : "false";
  } catch (e) {
    return "false";
  }
};

export const toggleGroupSelected = (
  answers: SurveyResponses,
  groupId: string,
  slug: string,
  setAnswer: SurveyAnswerFn,
  answer: string,
): Promise<SurveyAnswers | void> => {
  const cgSlug = `${slug}-collection-groups`;
  const res = JSON.parse(answers[cgSlug] ?? "[]").filter((id) => id != groupId);
  if (answer === "true") {
    res.push(groupId);
  }
  return setAnswer(cgSlug, JSON.stringify(res));
};

export const sensitiveDataQuestions = (
  groups: CollectionGroupDetailsDto[],
  initialPic?: Record<string, string[]>,
): SurveyQuestionProps[] => {
  return [
    physicalOrMentalHealthQuestion(),
    collectionGroupQuestion(PHYSICAL_MENTAL_HEALTH_CG_QUESTION_SLUG, groups),
    expandedSensitiveDataQuestion(),
    collectionGroupQuestion(EXPANDED_SENSITIVE_DATA_CG_QUESTION_SLUG, groups),
    ...groups.map((cg) => expandedSensitiveDataQuestionByGroup(cg, initialPic)),
    crimeVictimQuestion(),
    collectionGroupQuestion(VICTIM_OF_CRIME_CG_QUESTION_SLUG, groups),
    geofencingQuestion(),
    collectionGroupQuestion(GEOFENCING_CG_QUESTION_SLUG, groups),
  ];
};

export const sensitiveDataSurvey = (
  answers: Record<string, string>,
  groups: CollectionGroupDetailsDto[],
  initialPic?: Record<string, string[]>,
): SurveyDefinition => {
  return {
    questions: sensitiveDataQuestions(groups, initialPic),
    visibility: {
      [PHYSICAL_MENTAL_HEALTH_CG_QUESTION_SLUG]:
        answers[PHYSICAL_MENTAL_HEALTH_QUESTION_SLUG] == "true",
      [EXPANDED_SENSITIVE_DATA_CG_QUESTION_SLUG]:
        groups.length != 1 && answers[EXPANDED_SENSITIVE_DATA_QUESTION_SLUG] == "true",
      [VICTIM_OF_CRIME_CG_QUESTION_SLUG]: answers[VICTIM_OF_CRIME_QUESTION_SLUG] == "true",
      [GEOFENCING_CG_QUESTION_SLUG]: answers[GEOFENCING_QUESTION_SLUG] == "true",
      [EXPANDED_SENSITIVE_DATA_QUESTION_SLUG]: true,
    },
    disabled: {},
  };
};

export const sensitiveDataCollectionVisibilityMap = (
  answers: Record<string, string>,
  groups: CollectionGroupDetailsDto[],
): Record<string, Record<string, boolean>> => {
  return groups.reduce(
    (acc, cg) => ({
      ...acc,
      [consumerCollectionSurveyName(cg.id)]: {
        [SENSITIVE_DATA_COLLECTION_SLUG]: Boolean(
          (answers[EXPANDED_SENSITIVE_DATA_CG_QUESTION_SLUG]?.includes(cg.id) ||
            groups.length == 1) &&
            answers[EXPANDED_SENSITIVE_DATA_QUESTION_SLUG] == "true",
        ),
      },
    }),
    {},
  );
};

export const useSensitiveDataCollection = (
  orgId: OrganizationPublicId,
  collectionGroups: CollectionGroupDetailsDto[],
): {
  answers: Record<string, SurveyAnswers>;
  setAnswers: Record<string, SurveyAnswerFn>;
  request: NetworkRequestState;
  refresh: () => Promise<void>;
  invalidate: () => Promise<void[]>;
} => {
  const queryClient = useQueryClient();
  const [results, request, refresh] = useDataRequests(
    collectionGroups.map(({ id: groupId }) => {
      const surveyName = consumerCollectionSurveyName(groupId);
      return {
        queryKey: [surveyName, orgId],
        queryFn: () => Api.orgSurvey.getAnswersForSurvey(orgId, surveyName),
      };
    }),
  );

  const invalidate = async () =>
    Promise.all(
      collectionGroups.map(({ id: groupId }) => {
        const surveyName = consumerCollectionSurveyName(groupId);
        return queryClient.invalidateQueries([surveyName, orgId]);
      }),
    );

  const answers = useMemo(
    () =>
      results
        .filter((q) => {
          return q.status === "success";
        })
        .reduce((acc, q) => {
          acc[q.data.surveyName] = Object.keys(q.data.answers).reduce(
            (ans, key) => ({
              ...ans,
              [key]: q.data.answers[key].answer,
            }),
            {},
          );
          return acc;
        }, {}),
    [results],
  );

  return {
    answers,
    setAnswers: collectionGroups.reduce((acc, cur) => {
      const groupId = cur.id;
      acc[consumerCollectionSurveyName(groupId)] = async (
        slug: string,
        answer: string,
        question?: string,
      ) => {
        const surveyName = consumerCollectionSurveyName(groupId);
        await Api.orgSurvey.patchAnswersForSurvey(orgId, surveyName, [
          {
            slug,
            question,
            answer,
          },
        ]);
        return refresh();
      };
      return acc;
    }, {}),
    request,
    refresh,
    invalidate,
  };
};
